import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';

import {
	Box,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Tooltip as MuiTooltip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
	type XAxisTickContentProps,
} from 'recharts';

import useListSoldProductsByEvent from 'hooks/useListSoldProductsByEvent';
import useSalesByDate from 'hooks/useSalesByDate';
import { price, formatNumber } from 'lib/numbers';

const allStatuses = ['active', 'closed'];

const closedStatuses = ['closed'];

export default function SalesReport() {
	const { t } = useTranslation();
	// The last week by default: an empty range asks for the whole history (MUTE-610)
	const [from, setFrom] = useState<Moment | null>(() => moment().subtract(7, 'days').startOf('day'));
	const [to, setTo] = useState<Moment | null>(null);

	const fromIso = useMemo(() => (from ? from.toISOString() : undefined), [from]);
	const toIso = useMemo(() => (to ? to.toISOString() : undefined), [to]);

	// Chart line: all orders (active + closed) by createdAt
	const allChartSearchProps = useMemo(() => {
		const baseSearchProps = { statuses: allStatuses };
		if (fromIso || toIso) {
			return { ...baseSearchProps, from: fromIso, to: toIso };
		}
		return baseSearchProps;
	}, [fromIso, toIso]);

	// Chart bars + summary + event table: closed orders by confirmationDate
	const closedSearchProps = useMemo(() => {
		const baseSearchProps = { statuses: closedStatuses };
		if (fromIso || toIso) {
			return { ...baseSearchProps, confirmationFrom: fromIso, confirmationTo: toIso };
		}
		return baseSearchProps;
	}, [fromIso, toIso]);

	const { data: eventData, loading: eventsLoading } = useListSoldProductsByEvent(closedSearchProps);
	const { data: allSalesByDate, loading: allSalesLoading } = useSalesByDate(allChartSearchProps);
	const { data: closedSalesByDate, loading: closedSalesLoading } = useSalesByDate(closedSearchProps);

	// What was consumed with a card is not Mute's revenue: subtracted only on screen, from
	// the card part the API answers next to each total
	const [discountMuteCard, setDiscountMuteCard] = useState(false);
	const shown = useCallback(
		(total?: number, muteCardPart?: number) => (total ?? 0) - (discountMuteCard ? muteCardPart ?? 0 : 0),
		[discountMuteCard]
	);

	const stats = useMemo(() => {
		const salesTotal = allSalesByDate.reduce((sum, d) => sum + shown(d.totalPaid, d.nfcCardTotalPaid), 0);
		const deliveredTotal = closedSalesByDate.reduce((sum, d) => sum + shown(d.totalPaid, d.nfcCardTotalPaid), 0);
		const ordersTotal = allSalesByDate.reduce((sum, d) => sum + shown(d.totalOrders, d.nfcCardOrders), 0);
		const mediumTicket = ordersTotal > 0 ? salesTotal / ordersTotal : 0;
		const muteCardTotal = allSalesByDate.reduce((sum, d) => sum + (d.nfcCardTotalPaid ?? 0), 0);
		const muteCardOrders = allSalesByDate.reduce((sum, d) => sum + (d.nfcCardOrders ?? 0), 0);
		return { salesTotal, deliveredTotal, ordersTotal, mediumTicket, muteCardTotal, muteCardOrders };
	}, [allSalesByDate, closedSalesByDate, shown]);

	const chartData = useMemo(() => {
		const map = new Map<string, { name: string; delivered: number; sales: number }>();

		closedSalesByDate.forEach((item) => {
			map.set(item.date, { name: item.date, delivered: shown(item.totalPaid, item.nfcCardTotalPaid), sales: 0 });
		});

		allSalesByDate.forEach((item) => {
			const sales = shown(item.totalPaid, item.nfcCardTotalPaid);
			const existing = map.get(item.date);
			if (existing) {
				existing.sales = sales;
			} else {
				map.set(item.date, { name: item.date, delivered: 0, sales });
			}
		});

		return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
	}, [allSalesByDate, closedSalesByDate, shown]);

	const renderTick = (props: XAxisTickContentProps) => {
		const { x, y, index } = props;
		if (x == null || y == null || index == null) return null;
		// Show label every 3 days to avoid crowding
		if (index % 3 !== 0) return null;
		const label = chartData[index]?.name ?? '';
		if (!label) return null;

		const xPos = typeof x === 'number' ? x : Number(x);
		const yPos = typeof y === 'number' ? y : Number(y);
		if (Number.isNaN(xPos) || Number.isNaN(yPos)) return null;

		const parts = label.split('-');
		const display = parts.length === 3 ? `${parts[2]}/${parts[1]}` : label;
		return (
			<text x={xPos} y={yPos + 14} textAnchor="middle" fill="#666" fontSize={12}>
				{display}
			</text>
		);
	};

	return (
		<Box sx={{ marginTop: 4 }}>
			<Typography variant="h6" sx={{ mb: 3 }}>
				Reporte de ventas por evento
			</Typography>

			{/* Date range selector */}
			<Grid container spacing={2} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6} md={3}>
					<DateTimePicker
						sx={{ width: '100%' }}
						value={from}
						onChange={(newValue) => setFrom(newValue)}
						label="Desde"
						format="DD/MM/YYYY HH:mm"
						ampm={false}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<DateTimePicker
						sx={{ width: '100%' }}
						value={to}
						onChange={(newValue) => setTo(newValue)}
						minDateTime={from ?? undefined}
						label="Hasta"
						format="DD/MM/YYYY HH:mm"
						ampm={false}
					/>
				</Grid>
				<Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
					<MuiTooltip title={t('nfc:muteCardDiscountHelp')}>
						<FormControlLabel
							control={
								<Checkbox
									checked={discountMuteCard}
									onChange={(event) => setDiscountMuteCard(event.target.checked)}
								/>
							}
							label={t('nfc:muteCardDiscount')}
						/>
					</MuiTooltip>
				</Grid>
			</Grid>

			<Paper elevation={4} sx={{ padding: 2, mb: 3 }}>
				{/* Stats cards */}
				{allSalesLoading || closedSalesLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress />
					</Box>
				) : (
					<Grid container spacing={3} sx={{ mt: 2, mb: 6 }}>
						{[
							{
								icon: <TrendingUpIcon />,
								color: '#7c5cbf',
								value: price(stats.salesTotal),
								label: 'Total vendido',
							},
							{
								icon: <AttachMoneyIcon />,
								color: '#3dbf7c',
								value: price(stats.deliveredTotal),
								label: 'Total entregado',
							},
							{
								icon: <ConfirmationNumberIcon />,
								color: '#3db8bf',
								value: formatNumber(stats.ordersTotal, 0),
								label: 'Ventas',
							},
							{
								icon: <LocalAtmIcon />,
								color: '#bf8c3d',
								value: price(stats.mediumTicket),
								label: 'Ticket medio',
							},
							{
								icon: <CreditCardIcon />,
								color: '#9e9e9e',
								value: price(stats.muteCardTotal),
								label: `${t('nfc:muteCard')} (${formatNumber(stats.muteCardOrders, 0)} ventas)`,
							},
						].map(({ icon, color, value, label }) => (
							<Grid item xs={12} sm={6} md={4} lg key={label}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: '50%',
											backgroundColor: color,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											color: '#fff',
											flexShrink: 0,
										}}
									>
										{icon}
									</Box>
									<Box>
										<Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
											{value}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{label}
										</Typography>
									</Box>
								</Box>
							</Grid>
						))}
					</Grid>
				)}
			</Paper>

			<Paper elevation={4} sx={{ padding: 2, mb: 3 }}>
				{/* Bar chart */}
				<Box sx={{ mt: 4, mb: 2 }}>
					<Typography variant="subtitle1" sx={{ mb: 1 }}>
						<strong>Ingresos por día</strong>
					</Typography>
					{allSalesLoading || closedSalesLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : chartData.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							Sin datos para el período seleccionado
						</Typography>
					) : (
						<ResponsiveContainer width="100%" height={300}>
							<ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 60 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="name" interval={0} tick={renderTick} />
								<YAxis tickFormatter={(v) => price(v)} width={110} tick={{ fontSize: 12 }} />
								<Tooltip
									formatter={(value) => price(value as number)}
									labelFormatter={(label) => {
										const parts = (label as string).split('-');
										return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : label;
									}}
								/>
								<Bar dataKey="delivered" name="Entregados" fill="#1976d2" radius={[4, 4, 0, 0]} />
								<Line
									dataKey="sales"
									name="Ventas"
									type="monotone"
									stroke="#64d2c7"
									strokeWidth={2}
									dot={{ r: 3 }}
									activeDot={{ r: 5 }}
								/>
							</ComposedChart>
						</ResponsiveContainer>
					)}
				</Box>
			</Paper>

			<Paper elevation={4} sx={{ padding: 2, mb: 3 }}>
				{/* Table by event */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1 }}>
						<strong>Detalle por evento</strong>
					</Typography>
					{eventsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : eventData.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							Sin datos para el período seleccionado
						</Typography>
					) : (
						<Box sx={{ overflowX: 'auto' }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											<strong>Evento</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Fecha</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Órdenes</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Productos vendidos</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Total entregado</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Costo de servicio</strong>
										</TableCell>
										<TableCell align="right">
											<strong>Descuento</strong>
										</TableCell>
										<TableCell align="right">
											<strong>{t('nfc:muteCard')}</strong>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{eventData.map((row, idx) => (
										<TableRow key={row.eventId ?? idx} hover>
											<TableCell>{row.eventTitle}</TableCell>
											<TableCell align="right">
												{row.eventDate ? new Date(row.eventDate).toLocaleDateString('es-AR') : '-'}
											</TableCell>
											<TableCell align="right">
												{formatNumber(shown(row.totalOrders, row.nfcCardOrders), 0)}
											</TableCell>
											<TableCell align="right">
												{formatNumber(shown(row.totalProductsSold, row.nfcCardProductsSold), 0)}
											</TableCell>
											<TableCell align="right">{price(shown(row.totalPaid, row.nfcCardTotalPaid))}</TableCell>
											<TableCell align="right">
												{price(shown(row.totalSaleFee, row.nfcCardTotalSaleFee))}
											</TableCell>
											<TableCell align="right">
												{price(shown(row.totalSaleDiscount, row.nfcCardTotalSaleDiscount))}
											</TableCell>
											<TableCell align="right">{price(row.nfcCardTotalPaid ?? 0)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					)}
				</Box>
			</Paper>
		</Box>
	);
}
